import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Menu, Search, X } from "lucide-react";
import { buildWhatsAppLink, generalEnquireMessage } from "@/lib/whatsapp";
import { scrollToId } from "@/lib/scroll";

// "home" and "enquire" are homepage sections (scroll behavior). Everything
// else opens its own dedicated page — clicking Gold/Silver/Others takes you
// to the full catalog filtered to that category; Offers/Festival open the
// New Arrivals & Offers page. Nothing scrolls down one long homepage.
const NAV_LINKS = [
  { id: "home", label: "Home", type: "scroll" },
  { id: "gold", label: "Gold", type: "page", to: "/all-items?category=gold" },
  { id: "silver", label: "Silver", type: "page", to: "/all-items?category=silver" },
  { id: "others", label: "Others", type: "page", to: "/all-items?category=others" },
  { id: "offers", label: "Offers", type: "page", to: "/new-arrivals" },
  { id: "festival", label: "Festival", type: "page", to: "/new-arrivals" },
  { id: "enquire", label: "Enquire", type: "scroll" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (link) => {
    setOpen(false);
    if (link.type === "page") {
      navigate(link.to);
    } else {
      // "scroll" links need to work from any page — navigate home first if
      // we're not already there, then scroll (scrollToId already handles
      // this cross-route case for us).
      scrollToId(link.id);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    setSearchOpen(false);
    setOpen(false);
    if (q) navigate(`/all-items?search=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-card-lux"
          : "bg-gradient-to-b from-background/90 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <button onClick={() => handleNav({ id: "home", type: "scroll" })} className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="/images/jewellery/logo.png"
            alt="Vini Gems And Jewellers"
            className="h-11 w-11 sm:h-12 sm:w-12 object-contain rounded-full ring-1 ring-primary/40 group-hover:ring-primary transition-all duration-300"
          />
          <div className="flex flex-col leading-none text-left">
            <span className="font-heading text-base sm:text-lg font-semibold text-foreground tracking-wide">Vini Gems</span>
            <span className="font-sans text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-primary">And Jewellers</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNav(link)}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-300"
              data-testid={`nav-link-${link.id}`}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/new-arrivals"
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-300"
            data-testid="nav-link-new-arrivals"
          >
            New Arrivals
          </Link>
          <Link
            to="/all-items"
            className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-300"
            data-testid="nav-link-all-items"
          >
            All Items
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {searchOpen ? (
            <form onSubmit={handleSearchSubmit} className="relative animate-fade-up" data-testid="nav-search-form">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => !searchQuery && setSearchOpen(false)}
                placeholder="Search jewellery..."
                className="w-56 h-9 pl-9 pr-8 text-sm"
                data-testid="nav-search-input"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSearchOpen(false);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Close search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </form>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-card transition-colors"
              aria-label="Search"
              data-testid="nav-search-toggle-btn"
            >
              <Search className="h-4.5 w-4.5 text-foreground/80" />
            </button>
          )}
          <Button asChild variant="whatsapp" data-testid="nav-whatsapp-btn">
            <a href={buildWhatsAppLink(generalEnquireMessage())} target="_blank" rel="noopener noreferrer">
              <i className="fa-brands fa-whatsapp text-base" /> Enquire Now
            </a>
          </Button>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-border w-[280px]">
              <SheetTitle className="font-heading text-lg text-primary">Vini Gems And Jewellers</SheetTitle>
              <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
              <div className="flex flex-col gap-6 mt-8">
                <form onSubmit={handleSearchSubmit} className="relative" data-testid="mobile-nav-search-form">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search jewellery..."
                    className="pl-9"
                    data-testid="mobile-nav-search-input"
                  />
                </form>
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleNav(link)}
                    className="text-left font-heading text-xl text-foreground hover:text-primary transition-colors"
                    data-testid={`mobile-nav-link-${link.id}`}
                  >
                    {link.label}
                  </button>
                ))}
                <Link
                  to="/new-arrivals"
                  onClick={() => setOpen(false)}
                  className="text-left font-heading text-xl text-primary hover:text-primary/80 transition-colors"
                  data-testid="mobile-nav-link-new-arrivals"
                >
                  New Arrivals
                </Link>
                <Link
                  to="/all-items"
                  onClick={() => setOpen(false)}
                  className="text-left font-heading text-xl text-foreground hover:text-primary transition-colors"
                  data-testid="mobile-nav-link-all-items"
                >
                  All Items
                </Link>
                <Button asChild variant="whatsapp" className="mt-4">
                  <a href={buildWhatsAppLink(generalEnquireMessage())} target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-whatsapp" /> Enquire on WhatsApp
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
